import type { MarketId } from '@ps/isp-web-services-types';
import { noop, pick } from 'lodash';
import { useContext, useEffect, useRef } from 'react';
import { Subject } from 'rxjs';
import { map } from 'rxjs/operators';

import { MarketPollingContext } from '../providers/MarketPollingProvider';
import type { MarketPollingData } from '../types/market';

interface MarketPollingProps {
  marketIds: MarketId[];
  onMarketDataReceived?: (data: MarketPollingData) => void;
  skipAddingMarketIds?: boolean;
}

/**
 * Custom hook to manage market polling for bet slip opportunities.
 *
 * @param marketIds - Array of market IDs to poll.
 * @param onMarketDataReceived - Function to call with the polling data.
 * @param skipAddingMarketIds - Flag to skip adding market IDs to the polling context.
 */
const useMarketPolling = ({
  marketIds,
  onMarketDataReceived = noop,
  skipAddingMarketIds = false
}: MarketPollingProps) => {
  const subject = useRef(new Subject<MarketPollingData>());
  const { add, remove, marketsObservable } = useContext(MarketPollingContext);

  useEffect(() => {
    if (marketIds.length > 0 && !skipAddingMarketIds) {
      add(marketIds);

      return () => {
        remove(marketIds);
      };
    }
  }, [marketIds, add, remove, skipAddingMarketIds]);

  useEffect(() => {
    const observable = marketsObservable
      .pipe(
        map(({ data, closed }) => ({
          data: pick(data, marketIds),
          closed: marketIds.filter((id) => closed.includes(id))
        }))
      )
      .subscribe((data) => {
        subject.current.next(data);
      });

    // We're using a cold observable, that way we don't keep track of the previous values when initially subscribed
    const subscription = subject.current.subscribe(onMarketDataReceived);

    return () => {
      subscription.unsubscribe();
      observable.unsubscribe();
    };
  }, [onMarketDataReceived, marketIds, marketsObservable]);
};

export default useMarketPolling;
